import { useMemo, type ReactNode } from 'react';
import { inkOn } from '@core/palette';
import { Tooltip, type TipLine } from '@core/ui/Tooltip';
import { useProjectOrder } from './order';
import { usePins } from './pins';
import { useCanWrite } from '@core/auth/access';
import { useTable } from '@core/store';
import type { Row } from '@core/store/model';
import { dateRange, type ISODate } from '@core/dates';
import { GRAY, assigneeBackground, gradientColors } from '@core/people';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { CalendarPlus, Check, Crosshair, Eraser, EyeOff, Highlighter, PinOff, Plus, SquarePen, TreePalm } from 'lucide-react';
import { LANE_HEIGHT, assignSubRows, barGeometry, dateAtX, type Grid } from './geometry';
import { RowActions, rowAddButton } from './RowActions';
import { GridLines } from './GridHeader';
import { EVENT_DEFAULT_COLOR, vacationLabel, type Task } from './data';
import { spotBox, type Spot, type Spotlight } from './spotlight';

/** Вид пилюли события на полосе контекста и на Timeline: подтверждённое — заливка цветом, неподтверждённое — пунктир. */
export function eventPillLook(e: Pick<Row<'event'>, 'color' | 'confirmed'>): { tentative: boolean; className: string; style: Record<string, string> } {
  const color = e.color ?? EVENT_DEFAULT_COLOR;
  return e.confirmed
    ? { tentative: false, className: 'text-white', style: { background: color } }
    : { tentative: true, className: 'bg-white border-2 border-dashed', style: { borderColor: color, color } };
}

type Pill = { id: string; start_date: string; end_date: string; label: string; color: string; tip: { title: string; lines: TipLine[]; hint: string }; onOpen: () => void; menu?: MenuItem[]; look?: ReturnType<typeof eventPillLook> };
const dates = (p: { start_date: string; end_date: string }) => dateRange(p.start_date, p.end_date);
type Lane = { key: string; label: string; add?: () => void; addHere?: { label: string; icon: typeof Plus; at: (d: ISODate) => void }; pills: Pill[] };

function laneNodes(pills: Pill[], grid: Grid): { rows: number; nodes: ReactNode[] } {
  const visible = pills.filter((p) => barGeometry(p, grid) !== null);
  const sub = assignSubRows(visible);
  const rows = Math.max(1, ...[...sub.values()].map((r) => r + 1));
  const nodes = visible.map((p) => {
    const g = barGeometry(p, grid)!;
    const pill = (
      <Tooltip key={p.id} title={p.tip.title} lines={p.tip.lines} hint={p.tip.hint}>
      <span
        role="button" tabIndex={0} data-tentative={p.look?.tentative || undefined}
        className={`min-h-0 absolute text-[11px] leading-4 font-semibold px-1 truncate rounded-sm cursor-pointer z-[1] ${p.look?.className ?? ''}`}
        style={{ left: g.left + 1, width: Math.max(0, g.width - 2), top: (sub.get(p.id) ?? 0) * LANE_HEIGHT + 2, height: LANE_HEIGHT - 4, ...(p.look?.style ?? { background: p.color, color: inkOn(...gradientColors(p.color)) }), ...(p.look?.tentative ? { lineHeight: `${LANE_HEIGHT - 8}px` } : {}) }}
        onDoubleClick={p.onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter') p.onOpen(); }}
      >
        {p.label}
      </span>
      </Tooltip>
    );
    return p.menu ? <Menu key={p.id} items={p.menu}>{pill}</Menu> : pill;
  });
  return { rows, nodes };
}

/** Закреплённые задачи по проектам в порядке проектов — дорожка на проект. */
export function pinnedLanes<T extends { id: string; project_id: string | null; in_backlog: boolean }>(
  tasks: Iterable<T>, projects: { id: string; name: string }[], pins: ReadonlySet<string>,
): { project_id: string; name: string; tasks: T[] }[] {
  const byProject = new Map<string, T[]>();
  for (const t of tasks) if (pins.has(t.id) && !t.in_backlog && t.project_id) byProject.set(t.project_id, [...(byProject.get(t.project_id) ?? []), t]);
  return projects.filter((p) => byProject.has(p.id)).map((p) => ({ project_id: p.id, name: p.name, tasks: byProject.get(p.id)! }));
}

type Opts = {
  todayX: number | null;
  onEvent: (e: Row<'event'> | null, at?: ISODate) => void;          // null — новое; at — неделя под курсором
  onConfirmEvent: (e: Row<'event'>) => void;                        // неподтверждённый экземпляр серии — оставить как есть
  onVacation?: (v: Row<'vacation'> | null, at?: ISODate) => void;   // нет — дорожки отпусков нет
  pinned: { colors: Map<string, string[]>; onOpen: (t: Task) => void; onUnpin: (t: Task) => void; onJump: (t: Task) => void };
  spotlight: Spotlight;
  hidden: string[];                                         // ключи скрытых дорожек — личная настройка
  setHidden: (keys: string[]) => void;
};

export type ContextBand = { height: number; node: ReactNode; corner: ReactNode; hiddenLanes: { id: string; name: string }[] };

/** Полоса контекста под шапкой: события, отпуска, закреплённые задачи по проектам. corner — подписи дорожек (как подпроекты, разделители те же, что в теле); hiddenLanes — для меню Hidden в тулбаре. */
export function useContextBand(grid: Grid, { todayX, onEvent, onConfirmEvent, onVacation, pinned, spotlight, hidden, setHidden }: Opts): ContextBand {
  const events = useTable('event');
  const vacations = useTable('vacation');
  const people = useTable('person');
  const tasks = useTable('task');
  const { rows: orderedProjects } = useProjectOrder();
  const projectOrder = useMemo(() => orderedProjects.map((p) => ({ id: p.id, name: p.name })), [orderedProjects]);
  const pins = usePins();
  const canWrite = useCanWrite();
  return useMemo(() => {
    const hideLane = (key: string, label: string, suffix = ''): MenuItem => ({ label: `Hide ${label} lane${suffix}`, onSelect: () => setHidden([...hidden, key]), icon: EyeOff });
    const highlightItem = (s: Spot): MenuItem[] => [{ label: spotlight.is(s.kind, s.id) ? 'Remove the highlight' : 'Highlight', onSelect: () => spotlight.toggle(s), icon: spotlight.is(s.kind, s.id) ? Eraser : Highlighter }];
    const all: Lane[] = [];
    all.push({
      key: 'events', label: 'Events', ...(canWrite ? { add: () => onEvent(null), addHere: { label: 'New event here', icon: CalendarPlus, at: (d: ISODate) => onEvent(null, d) } } : {}),
      pills: [...events.values()].map((e) => ({
        id: e.id, start_date: e.start_date, end_date: e.end_date, label: e.name, color: e.color ?? EVENT_DEFAULT_COLOR, look: eventPillLook(e),
        tip: { title: e.name, lines: [e.sublabel ?? '', dates(e), ...(e.confirmed ? [] : [{ text: 'Part of a series, not confirmed yet', tone: 'warn' as const }])], hint: canWrite ? 'Double-click to edit' : 'Double-click to open' }, onOpen: () => onEvent(e),
        menu: [
          { label: canWrite ? 'Edit event' : 'Open event', onSelect: () => onEvent(e), icon: SquarePen },
          ...(e.confirmed || !canWrite ? [] : [{ label: 'Confirm', onSelect: () => onConfirmEvent(e), icon: Check }]),
          ...highlightItem({ kind: 'event', id: e.id }), 'sep', hideLane('events', 'Events'),
        ],
      })),
    });
    if (onVacation) {
      all.push({
        key: 'vacations', label: 'Vacations', ...(canWrite ? { add: () => onVacation(null), addHere: { label: 'New vacation here', icon: TreePalm, at: (d: ISODate) => onVacation(null, d) } } : {}),
        pills: [...vacations.values()].map((v) => {
          const p = people.get(v.person_id);
          return {
            id: v.id, start_date: v.start_date, end_date: v.end_date, label: p?.name ?? 'Vacation', color: p?.color ?? GRAY,
            tip: { title: vacationLabel(v, p), lines: [dates(v)], hint: canWrite ? 'Double-click to edit' : 'Double-click to open' }, onOpen: () => onVacation(v),
            menu: [{ label: canWrite ? 'Edit vacation' : 'Open vacation', onSelect: () => onVacation(v), icon: SquarePen }, ...highlightItem({ kind: 'vacation', id: v.id }), 'sep', hideLane('vacations', 'Vacations')],
          };
        }),
      });
    }
    for (const lane of pinnedLanes([...tasks.values()], projectOrder, pins)) {
      const key = `project:${lane.project_id}`;
      all.push({
        key, label: lane.name,
        pills: lane.tasks.map((t) => ({
          id: t.id, start_date: t.start_date, end_date: t.end_date, label: t.title, color: assigneeBackground(pinned.colors.get(t.id) ?? []),
          tip: { title: t.title, lines: [`${lane.name} · pinned`, dates(t)], hint: 'Double-click to open' }, onOpen: () => pinned.onOpen(t),
          menu: [
            { label: 'Open', onSelect: () => pinned.onOpen(t), icon: SquarePen },
            { label: 'Jump to the task', onSelect: () => pinned.onJump(t), icon: Crosshair },
            ...highlightItem({ kind: 'task', id: t.id }),
            'sep',
            { label: 'Unpin', shortcut: 'Ctrl+P', onSelect: () => pinned.onUnpin(t), icon: PinOff },
            hideLane(key, lane.name),
          ],
        })),
      });
    }
    const lanes = all.filter((l) => !hidden.includes(l.key)).map((l) => ({ ...l, ...laneNodes(l.pills, grid) }));
    const hiddenLanes = all.filter((l) => hidden.includes(l.key));
    const height = lanes.reduce((h, l) => h + l.rows * LANE_HEIGHT, 0);
    let top = 0;
    const node = (
      <div className="relative bg-gray-50" style={{ height }}>
        <GridLines grid={grid} todayX={todayX} spot={spotBox(spotlight.range, grid)} />
        {lanes.map((l) => {
          const y = top; top += l.rows * LANE_HEIGHT;
          // правый клик по пустому месту дорожки: новое событие или отпуск на неделе под курсором, скрыть дорожку
          const at = { current: null as ISODate | null };
          const items: MenuItem[] = [
            ...(l.addHere ? [{ label: l.addHere.label, icon: l.addHere.icon, onSelect: () => { if (at.current) l.addHere!.at(at.current); } }] : []),
            hideLane(l.key, l.label, ' (only for you)'),
          ];
          return (
            <Menu key={l.key} items={items}>
              <div data-lane={l.key} className={`absolute inset-x-0 ${y ? 'border-t border-gray-300' : ''}`} style={{ top: y, height: l.rows * LANE_HEIGHT }}
                onContextMenu={(e) => { const r = e.currentTarget.getBoundingClientRect(); at.current = dateAtX(e.clientX - r.left, grid); }}>
                {l.nodes}
              </div>
            </Menu>
          );
        })}
      </div>
    );
    const corner = (
      <div className="bg-gray-50" style={{ height }}>
        {lanes.map((l, i) => {
          const one = l.label.toLowerCase().replace(/s$/, '');
          const items: MenuItem[] = [
            ...(l.add ? [{ label: `Add ${one}`, onSelect: l.add, icon: Plus }] : []),
            hideLane(l.key, l.label, ' (only for you)'),
          ];
          return (
            <Menu key={l.key} items={items}>
            <div data-lane-label={l.key} className={`group relative flex items-center pl-6 pr-2 text-sm text-gray-600 ${i ? 'border-t border-gray-300' : ''}`} style={{ height: l.rows * LANE_HEIGHT }}>
              <span className="truncate" title={l.label}>{l.label}</span>
              {l.add && <RowActions bg="bg-gray-50"><button type="button" className={rowAddButton} onClick={l.add} aria-label={`Add ${one}`} title={`Add ${one}`}>+</button></RowActions>}
            </div>
            </Menu>
          );
        })}
      </div>
    );
    return { height, node, corner, hiddenLanes: hiddenLanes.map((l) => ({ id: l.key, name: l.label })) };
  }, [events, vacations, people, tasks, projectOrder, pins, grid, todayX, onEvent, onConfirmEvent, onVacation, pinned, spotlight, hidden, setHidden, canWrite]);
}

